/*!
 * EGT — /classicman  (THE CLASSIC MAN PODCAST — a STANDALONE landing surface).
 *
 * A rebuild of the old, broken static app/public/classicman.html to the egt.studio
 * craft bar. STANDALONE identity: its own frosted header + logo/wordmark and a hero
 * built on the show's real banner art — NOT the EGT room nav. It stands on its own,
 * but reuses the SHARED EGT FOOTER (S.footer) so the brand/legal chrome stays
 * consistent site-wide.
 *
 * Because it is standalone this element does NOT call S.page() (that renders the EGT
 * room nav + announce bar). It composes its own `.egt.egt-cm` root: its own header,
 * the sections, then the shared footer + the capture/nav client scripts the footer's
 * capture door needs. The page JSON adds /src/egt.home.js which drives .egt-reveal,
 * the reading-progress hairline and [data-egt-parallax] — all keyed off classes/attrs.
 *
 * BRAND (agents.md §4): gold #C48E2F scarce on true black #0C0C0C; Playfair (display)
 * + Inter (body); FROSTED GLASS over UNDIMMED media — never a scrim; rem-only; brand
 * tokens via var(--egt-*). Masculine, classic, cinematic, timeless.
 *
 * ALL content — the two hosts' bios, handles, and every subscribe / social / donate
 * link — is preserved VERBATIM from the source file (only the "elevat_" typo in the
 * retired footer was dropped along with that footer). The email capture reuses the
 * shared double-opt-in S.capture (source "classicman" → the ONE `general` newsletter),
 * NOT the Exploit free-sample delivery.
 */

var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.classicman",
  title: "Classic Man Podcast",
  description: "The Classic Man Podcast — healthy, non-toxic masculinity with hosts Isaiah Carter and Alex Martinez. Listen on Spotify, Apple Podcasts and YouTube.",
  supportServer: true
});
var S = require("../helpers/egt.sections.js");
var Capture = require("../helpers/egt.capture.js");

/* ── real, verbatim data ─────────────────────────────────────────────── */

/* The three listening platforms + the in-page Donate anchor. Exact hrefs from
 * app/public/classicman.html. Donate scrolls to #support (the Stripe buy button),
 * exactly as the source's `href="#donate"` did. */
var SUBSCRIBE = [
  { id: "spotify", label: "Spotify", sub: "Listen on Spotify",
    href: "https://open.spotify.com/show/18dQrxKHGG3Pd7uMebaF39", icon: "bi-spotify", ext: true },
  { id: "apple", label: "Apple Podcasts", sub: "Listen on Apple Podcasts",
    href: "https://podcasts.apple.com/us/podcast/classic-man-isaiah-carter-youngcoachcarter/id1824776528", icon: "bi-apple", ext: true },
  { id: "youtube", label: "YouTube", sub: "Watch on YouTube",
    href: "https://www.youtube.com/@ClassicManPodcast", icon: "bi-youtube", ext: true },
  { id: "donate", label: "Donate", sub: "Support the show",
    href: "#support", icon: "bi-heart-fill", ext: false }
];

/* The three LISTEN CTAs only (no Donate) — the hero + listen rails. */
var PLATFORMS = SUBSCRIBE.filter(function (s) { return s.id !== "donate"; });

var HOSTS = [
  {
    name: "Isaiah Carter",
    handle: "@youngcoachcarter",
    role: "Host",
    photo: "/imgs/isaiah-carter.png",
    alt: "Isaiah Carter, co-host of the Classic Man Podcast",
    bio: "Isaiah Carter (@youngcoachcarter) is more than a bodybuilder—he's a builder of people. A national champion powerlifter with a preacher's heart and a soldier's discipline, Isaiah has dedicated his life to service: to youth, his community, and his faith. From high school sidelines to AAU courts, from the weight room to the pulpit, he's guided by one mission—helping others grow stronger in body, mind, and spirit. A reserve military police turned social worker and Family Life Pastor, Isaiah brings raw authenticity and hard-earned wisdom to every role he takes on. Off-duty, he's a swole MMA enthusiast, brother, and a proud dog dad.",
    socials: [
      { label: "Isaiah Carter on Instagram", href: "https://www.instagram.com/youngcoachcarter", icon: "bi-instagram" },
      { label: "Isaiah Carter on Facebook", href: "https://www.facebook.com/isaiah.carter.77", icon: "bi-facebook" }
    ]
  },
  {
    name: "Alex Martinez",
    handle: "@ghoenhard",
    role: "Host",
    photo: "/imgs/alex-martinez.jpg",
    alt: "Alex Martinez, co-host of the Classic Man Podcast",
    bio: "Alex Martinez (@ghoenhard) is a protector at heart—on the front lines, behind the lens, and in the lives he touches. A veteran firefighter and two-tour combat veteran, Alex has spent his life answering the call—whether it's in combat zones or burning buildings. Now a devoted husband and father of five, he balances family life with entrepreneurship, running a thriving photography and videography business that captures truth through his creative eye. A committed Christian, he's a steady presence—strong, grounded, and always ready to serve.",
    socials: [
      { label: "Alex Martinez on Instagram", href: "https://www.instagram.com/ghoenhard", icon: "bi-instagram" },
      { label: "Alex Martinez on Facebook", href: "https://www.facebook.com/stew.pedazz.7", icon: "bi-facebook" },
      { label: "IGY6 Photography & Design on Facebook", href: "https://www.facebook.com/people/IGY6-Photography-Design/61555906425689", icon: "bi-camera-fill" }
    ]
  }
];

/* The mission line — verbatim from the source's dark card. */
var MISSION = "With so many influencers promoting toxic versions of masculinity, I want to be the healthy voice in the mix. Teaching you that classic and timeless doesn't have to mean toxic. Let me help you heal, grow, and be the best version of yourself.";

/* ── header (standalone) ─────────────────────────────────────────────── */
function subscribeButton(s, cls) {
  return {
    n: "a", c: "egt-cm__sub" + (cls ? " " + cls : ""),
    href: s.href,
    target: s.ext ? "_blank" : undefined,
    rel: s.ext ? "noopener noreferrer" : undefined,
    i: [
      { n: "i", c: s.icon + " egt-cm__sub-icon", "aria-hidden": "true" },
      { n: "span", c: "egt-cm__sub-txt",
        i: [ { n: "span", c: "egt-cm__sub-label", i: s.label },
          { n: "span", c: "egt-cm__sub-sub", i: s.sub } ] }
    ]
  };
}

function header() {
  var nav = [
    { n: "a", href: "#hosts", c: "egt-cm__nav-link", i: "The Hosts" },
    { n: "a", href: "#listen", c: "egt-cm__nav-link", i: "Listen" },
    { n: "a", href: "#support", c: "egt-cm__nav-link", i: "Support" }
  ];
  return {
    n: "header", c: "egt-cm__header",
    i: { n: "div", c: "egt__shell egt-cm__header-inner",
      i: [
        { n: "a", href: "#top", c: "egt-cm__brand", "aria-label": "Classic Man Podcast — home",
          i: [
            { n: "img", c: "egt-cm__brand-logo", src: "/imgs/classic-man-logo.png",
              alt: "", width: "48", height: "48", decoding: "async" },
            { n: "span", c: "egt-cm__brand-txt",
              i: [ { n: "span", c: "egt-cm__brand-name", i: "Classic Man" },
                { n: "span", c: "egt-cm__brand-sub", i: "Podcast" } ] }
          ] },
        { n: "nav", c: "egt-cm__nav", "aria-label": "Sections", i: nav },
        { n: "a", href: "#listen", c: "egt-cm__header-cta",
          i: [ { n: "i", c: "bi-broadcast", "aria-hidden": "true" }, " Listen" ] }
      ] }
  };
}

/* ── hero (undimmed banner + frosted card) ───────────────────────────── */
function hero() {
  var card = {
    n: "div", c: "egt-cm__hero-card egt-glass egt-reveal",
    i: [
      { n: "p", c: "egt-cm__eyebrow", i: "Healthy, non-toxic masculinity" },
      { n: "h1", c: "egt-cm__hero-title",
        i: [ "Classic ", { n: "span", c: "egt-cm__gold", i: "Man" }, " ", { n: "br" }, "Podcast" ] },
      { n: "p", c: "egt-cm__hero-lead",
        i: "Classic and timeless doesn't have to mean toxic. Let me help you heal, grow, and be the best version of yourself." },
      /* answer-first (SEO wave 2C-5): what the show is, who hosts it, who it's for */
      { n: "p", c: "egt-cm__hero-answer",
        i: "The Classic Man Podcast is a show about healthy, non-toxic masculinity, hosted by Isaiah Carter and Alex Martinez — for men who want to heal, grow and become the best version of themselves. Listen on Spotify, Apple Podcasts and YouTube." },
      { n: "div", c: "egt-cm__hero-actions",
        i: PLATFORMS.map(function (p) { return subscribeButton(p, "egt-cm__sub--hero"); }) }
    ]
  };
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-cm__hero", id: "top",
    i: [
      S.media({ img: "/imgs/classic-man-banner.png", alt: "The Classic Man Podcast", parallax: 0.08, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-cm__hero-inner", i: card }
    ]
  };
}

/* ── mission pull-quote ──────────────────────────────────────────────── */
function mission() {
  return {
    n: "section", c: "egt-cm__mission",
    i: { n: "blockquote", c: "egt__shell egt-cm__mission-inner egt-reveal",
      i: [
        { n: "span", c: "egt-cm__mission-mark", "aria-hidden": "true", i: "“" },
        { n: "p", c: "egt-cm__mission-text", i: MISSION },
        { n: "footer", c: "egt-cm__mission-attr", i: "The Classic Man Podcast" }
      ] }
  };
}

/* ── hosts ───────────────────────────────────────────────────────────── */
function hostCard(h, idx) {
  return {
    n: "article", c: "egt-cm__host egt-glass egt-reveal egt-reveal--up", style: "--egt-i:" + idx,
    i: [
      { n: "div", c: "egt-cm__host-media",
        i: { n: "img", c: "egt-cm__host-img", src: h.photo, alt: h.alt, loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt-cm__host-body",
        i: [
          { n: "p", c: "egt-cm__host-role", i: h.role },
          { n: "h3", c: "egt-cm__host-name", i: h.name },
          { n: "p", c: "egt-cm__host-handle", i: h.handle },
          { n: "p", c: "egt-cm__host-bio", i: h.bio },
          { n: "ul", c: "egt-cm__host-socials",
            i: h.socials.map(function (s) {
              return { n: "li", i: { n: "a", c: "egt-cm__host-social", href: s.href,
                target: "_blank", rel: "noopener noreferrer", "aria-label": s.label,
                i: { n: "i", c: s.icon, "aria-hidden": "true" } } };
            }) }
        ] }
    ]
  };
}

function hosts() {
  return {
    n: "section", c: "egt-section egt-cm__hosts", id: "hosts",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-cm__head egt-reveal",
          i: [ { n: "p", c: "egt-cm__eyebrow", i: "Your hosts" },
            { n: "h2", c: "egt-cm__h2", i: [ "Two men. ", { n: "span", c: "egt-cm__gold", i: "One mission." } ] },
            { n: "p", c: "egt-cm__head-lead",
              i: "Strength with a conscience — service, faith, and the hard-won kind of wisdom you can only earn." } ] },
        { n: "div", c: "egt-cm__host-grid", i: HOSTS.map(hostCard) }
      ] }
  };
}

/* ── listen / subscribe (buttons + real embeds) ──────────────────────── */
function listen() {
  var appleEmbed = {
    n: "div", c: "egt-cm__embed egt-cm__embed--apple egt-reveal",
    i: { n: "iframe", c: "egt-cm__embed-frame", title: "Classic Man Podcast — Apple Podcasts player",
      src: "https://embed.podcasts.apple.com/us/podcast/classic-man-isaiah-carter-youngcoachcarter/id1824776528?itscg=30200&itsct=podcast_box_player&ls=1&theme=dark",
      height: "450", loading: "lazy",
      sandbox: "allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation",
      allow: "autoplay *; encrypted-media *; clipboard-write" } };
  var ytEmbed = {
    n: "div", c: "egt-cm__embed egt-cm__embed--yt egt-reveal",
    i: { n: "iframe", c: "egt-cm__embed-frame", title: "Classic Man Podcast — YouTube episodes",
      src: "https://www.youtube.com/embed/videoseries?list=PLW1r2o1rPudXKLj95yrZFIpIpitSD7Som",
      loading: "lazy", frameborder: "0",
      allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
      referrerpolicy: "strict-origin-when-cross-origin", allowfullscreen: "allowfullscreen" } };
  return {
    n: "section", c: "egt-section egt-cm__listen", id: "listen",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-cm__head egt-reveal",
          i: [ { n: "p", c: "egt-cm__eyebrow", i: "Listen & subscribe" },
            { n: "h2", c: "egt-cm__h2", i: [ "Press ", { n: "span", c: "egt-cm__gold", i: "play." } ] },
            { n: "p", c: "egt-cm__head-lead",
              i: "New episodes on every major platform — pick where you already listen." } ] },
        { n: "div", c: "egt-cm__sub-rail egt-reveal",
          i: SUBSCRIBE.map(function (s) { return subscribeButton(s); }) },
        { n: "div", c: "egt-cm__embeds", i: [ appleEmbed, ytEmbed ] }
      ] }
  };
}

/* ── support / donate (the Stripe buy button, preserved verbatim) ─────── */
function support() {
  return {
    n: "section", c: "egt-section egt-cm__support", id: "support",
    i: { n: "div", c: "egt__shell",
      i: { n: "div", c: "egt-cm__support-card egt-glass egt-reveal",
        i: [
          { n: "p", c: "egt-cm__eyebrow", i: "Support the show" },
          { n: "h2", c: "egt-cm__h2", i: [ "Keep it ", { n: "span", c: "egt-cm__gold", i: "on the air." } ] },
          { n: "p", c: "egt-cm__support-lead",
            i: "The Classic Man Podcast is independent. If it's helped you heal, grow, or show up better — a gift of any size keeps the mics on." },
          { n: "div", c: "egt-cm__stripe",
            i: [
              { n: "script", async: "async", src: "https://js.stripe.com/v3/buy-button.js" },
              { n: "stripe-buy-button",
                "buy-button-id": "buy_btn_1RicqOJXPaDKkYy0K2AYRwvZ",
                "publishable-key": "pk_live_OaTlMM3BpQoxFLMP7j3LGI4P" }
            ] }
        ] } }
  };
}

/* ── stay connected (shared double-opt-in capture) ───────────────────── */
function connect() {
  return {
    n: "section", c: "egt-section egt-cm__connect", id: "connect",
    i: { n: "div", c: "egt__shell",
      i: Capture.capture({
        source: "classicman",
        id: "egt-cap-classicman",
        variant: "panel",
        consent: true,
        eyebrow: "Stay connected",
        title: [ "Don't miss an ", { n: "span", c: "egt-cm__gold", i: "episode." } ],
        lead: "Get a heads-up when new episodes drop, plus the occasional note from the show.",
        label: "Your email address",
        cta: "Keep me posted",
        consentLabel: "Yes — email me when new Classic Man episodes and news drop. I can unsubscribe from any email."
      }) }
  };
}

/* ── sponsor strip (above the shared EGT footer) ─────────────────────── */
function sponsor() {
  return {
    n: "section", c: "egt-cm__sponsor",
    i: { n: "p", c: "egt__shell egt-cm__sponsor-inner egt-reveal",
      i: [ "The Classic Man Podcast is presented by ",
        { n: "a", href: "/", c: "egt-cm__sponsor-link", i: "The Universe" },
        " by EGT." ] }
  };
}

/* ── per-surface schema (PodcastSeries) ──────────────────────────────── */
function classicmanSchema() {
  var O = S.siteOrigin();
  return S.jsonLd([
    { "@type": "WebPage", "@id": O + "/classicman#webpage", url: O + "/classicman",
      name: "Classic Man Podcast",
      description: "The Classic Man Podcast — healthy, non-toxic masculinity with hosts Isaiah Carter and Alex Martinez.",
      isPartOf: { "@id": O + "/#website" },
      about: { "@id": O + "/classicman#podcast" } },
    { "@type": "PodcastSeries", "@id": O + "/classicman#podcast",
      name: "Classic Man Podcast",
      description: "Healthy, non-toxic masculinity — heal, grow, and be the best version of yourself.",
      url: O + "/classicman",
      /* The show's real RSS feed (Apple's own lookup feedUrl), not the Apple
       * Podcasts web page. Publisher = EGT, matching the visible "presented by
       * The Universe by EGT" line. SEO wave 2B-5, 2026-10-09. */
      webFeed: "https://anchor.fm/s/106e5a3ec/podcast/rss",
      publisher: { "@id": O + "/#organization" },
      sameAs: [
        "https://open.spotify.com/show/18dQrxKHGG3Pd7uMebaF39",
        "https://podcasts.apple.com/us/podcast/classic-man-isaiah-carter-youngcoachcarter/id1824776528",
        "https://www.youtube.com/@ClassicManPodcast"
      ],
      actor: HOSTS.map(function (h) { return { "@type": "Person", name: h.name }; }) }
  ]);
}

/* Reused by the podcast show page (/podcasts/classic-man) so the live sign-up and
 * donate button carry over unchanged when /classicman moves there (spec 35). */
exports.support = support;
exports.connect = connect;

exports.render = async function render() {
  var sections = [
    classicmanSchema(),
    header(),
    hero(),
    mission(),
    hosts(),
    listen(),
    support(),
    connect(),
    sponsor()
  ];
  /* Standalone: build our OWN `.egt` root (NOT S.page — no EGT room nav / announce
   * bar), but append the SHARED footer + the client scripts the footer capture and
   * site schema need. /src/egt.home.js (added by the page JSON) drives .egt-reveal,
   * the reading-progress hairline and parallax. */
  return {
    n: "main", c: "egt egt-cm",
    i: [ S.readingProgress() ]
      .concat(sections)
      .concat([ S.footer({ footerCaptureLabel: S.FOOTER_LABELS.classicman }), Capture.captureScript(), S.siteSchema() ])
  };
};
